import _objectWithoutProperties from "/vercel/path0/node_modules/@babel/runtime/helpers/esm/objectWithoutProperties.js";
import _defineProperty from "/vercel/path0/node_modules/@babel/runtime/helpers/esm/defineProperty.js";
import _slicedToArray from "/vercel/path0/node_modules/@babel/runtime/helpers/esm/slicedToArray.js";
import _toConsumableArray from "/vercel/path0/node_modules/@babel/runtime/helpers/esm/toConsumableArray.js";
var _excluded = ["lockScroll"];
import "core-js/modules/es.array.flat.js";
import "core-js/modules/es.array.unscopables.flat.js";
function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }
function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }
import * as React from 'react';
import React__default, { useLayoutEffect, useEffect, useContext, useMemo, useRef, useCallback, useState, createContext, forwardRef, cloneElement } from 'react';
import { getOverflowAncestors, useFloating as useFloating$1 } from '@floating-ui/react-dom';
export * from '@floating-ui/react-dom';
import pointInPolygon from 'point-in-polygon';
import { createPortal } from 'react-dom';
import { hideOthers } from 'aria-hidden';
var index = typeof document !== 'undefined' ? useLayoutEffect : useEffect;
function createPubSub() {
  var map = new Map();
  return {
    emit: function emit(event, data) {
      var _map$get;
      (_map$get = map.get(event)) == null ? void 0 : _map$get.forEach(function (handler) {
        return handler(data);
      });
    },
    on: function on(event, listener) {
      map.set(event, [].concat(_toConsumableArray(map.get(event) || []), [listener]));
    },
    off: function off(event, listener) {
      map.set(event, (map.get(event) || []).filter(function (l) {
        return l !== listener;
      }));
    }
  };
}
var serverHandoffComplete = false;
var count = 0;
var genId = function genId() {
  return "floating-ui-" + count++;
};
function useFloatingId() {
  var _React$useState = React.useState(function () {
      return serverHandoffComplete ? genId() : undefined;
    }),
    _React$useState2 = _slicedToArray(_React$useState, 2),
    id = _React$useState2[0],
    setId = _React$useState2[1];
  index(function () {
    if (id == null) {
      setId(genId());
    } // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  React.useEffect(function () {
    if (!serverHandoffComplete) {
      serverHandoffComplete = true;
    }
  }, []);
  return id;
} // `toString()` prevents bundlers from trying to `import { useId } from 'react'`

var useReactId = React[/*#__PURE__*/'useId'.toString()];
/**
 * Uses React 18's built-in `useId()` when available, or falls back to a
 * slightly less performant (requiring a double render) implementation for
 * earlier React versions.
 * @see https://floating-ui.com/docs/useId
 */

var useId = useReactId != null ? useReactId : useFloatingId;
var FloatingNodeContext = /*#__PURE__*/createContext(null);
var FloatingTreeContext = /*#__PURE__*/createContext(null);
var useFloatingParentNodeId = function useFloatingParentNodeId() {
  var _useContext$id, _useContext;
  return (_useContext$id = (_useContext = useContext(FloatingNodeContext)) == null ? void 0 : _useContext.id) != null ? _useContext$id : null;
};
var useFloatingTree = function useFloatingTree() {
  return useContext(FloatingTreeContext);
};
/**
 * Registers a node into the floating tree, returning its id.
 */

var useFloatingNodeId = function useFloatingNodeId() {
  var id = useId();
  var tree = useFloatingTree();
  var parentId = useFloatingParentNodeId();
  index(function () {
    var node = {
      id: id,
      parentId: parentId
    };
    tree == null ? void 0 : tree.addNode(node);
    return function () {
      tree == null ? void 0 : tree.removeNode(node);
    };
  }, [tree, id, parentId]);
  return id;
};
/**
 * Provides parent node context for nested floating elements.
 * @see https://floating-ui.com/docs/FloatingTree
 */

var FloatingNode = function FloatingNode(_ref) {
  var children = _ref.children,
    id = _ref.id;
  var parentId = useFloatingParentNodeId();
  return /*#__PURE__*/React__default.createElement(FloatingNodeContext.Provider, {
    value: useMemo(function () {
      return {
        id: id,
        parentId: parentId
      };
    }, [id, parentId])
  }, children);
};
/**
 * Provides context for nested floating elements when they are not children of
 * each other on the DOM (i.e. portalled to a common node, rather than their
 * respective parent).
 * @see https://floating-ui.com/docs/FloatingTree
 */

var FloatingTree = function FloatingTree(_ref2) {
  var children = _ref2.children;
  var nodesRef = useRef([]);
  var addNode = useCallback(function (node) {
    nodesRef.current = [].concat(_toConsumableArray(nodesRef.current), [node]);
  }, []);
  var removeNode = useCallback(function (node) {
    nodesRef.current = nodesRef.current.filter(function (n) {
      return n !== node;
    });
  }, []);
  var events = useState(function () {
    return createPubSub();
  })[0];
  return /*#__PURE__*/React__default.createElement(FloatingTreeContext.Provider, {
    value: useMemo(function () {
      return {
        nodesRef: nodesRef,
        addNode: addNode,
        removeNode: removeNode,
        events: events
      };
    }, [nodesRef, addNode, removeNode, events])
  }, children);
};
function mergeProps(userProps, propsList, elementKey) {
  var fnsMap = {};
  return _objectSpread(_objectSpread(_objectSpread({}, elementKey === 'floating' && {
    tabIndex: -1
  }), userProps), propsList.map(function (value) {
    return value ? value[elementKey] : null;
  }).concat(userProps).reduce(function (acc, props) {
    if (!props) {
      return acc;
    }
    Object.entries(props).forEach(function (_ref) {
      var _ref5 = _slicedToArray(_ref, 2),
        key = _ref5[0],
        value = _ref5[1];
      if (key.indexOf('on') === 0) {
        if (!fnsMap[key]) {
          fnsMap[key] = [];
        }
        if (typeof value === 'function') {
          fnsMap[key].push(value);
        }
        acc[key] = function () {
          for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
            args[_key] = arguments[_key];
          }
          fnsMap[key].forEach(function (fn) {
            return fn.apply(void 0, args);
          });
        };
      } else {
        acc[key] = value;
      }
    });
    return acc;
  }, {}));
}
var useInteractions = function useInteractions(propsList) {
  if (propsList === void 0) {
    propsList = [];
  }
  return {
    getReferenceProps: function getReferenceProps(userProps) {
      return mergeProps(userProps, propsList, 'reference');
    },
    getFloatingProps: function getFloatingProps(userProps) {
      return mergeProps(userProps, propsList, 'floating');
    },
    getItemProps: function getItemProps(userProps) {
      return mergeProps(userProps, propsList, 'item');
    }
  };
};
function getDocument(floating) {
  var _floating$ownerDocume;
  return (_floating$ownerDocume = floating == null ? void 0 : floating.ownerDocument) != null ? _floating$ownerDocume : document;
}
function getWindow(value) {
  var _getDocument$defaultV;
  return (_getDocument$defaultV = getDocument(value).defaultView) != null ? _getDocument$defaultV : window;
}
function isElement(value) {
  return value ? value instanceof getWindow(value).Element : false;
}
function isHTMLElement(value) {
  return value ? value instanceof getWindow(value).HTMLElement : false;
}
function getChildren(tree, id) {
  var _tree$nodesRef$curren;
  var allChildren = (_tree$nodesRef$curren = tree == null ? void 0 : tree.nodesRef.current.filter(function (node) {
    var _node$context;
    return node.parentId === id && ((_node$context = node.context) == null ? void 0 : _node$context.open);
  })) != null ? _tree$nodesRef$curren : [];
  var currentChildren = allChildren;
  while (currentChildren.length) {
    var _tree$nodesRef$curren2;
    currentChildren = (_tree$nodesRef$curren2 = tree == null ? void 0 : tree.nodesRef.current.filter(function (node) {
      var _currentChildren;
      return (_currentChildren = currentChildren) == null ? void 0 : _currentChildren.some(function (n) {
        var _node$context2;
        return node.parentId === n.id && ((_node$context2 = node.context) == null ? void 0 : _node$context2.open);
      });
    })) != null ? _tree$nodesRef$curren2 : [];
    allChildren = allChildren.concat(currentChildren);
  }
  return allChildren;
}
function safePolygon(_temp) {
  var _ref6 = _temp === void 0 ? {} : _temp,
    _ref6$restMs = _ref6.restMs,
    restMs = _ref6$restMs === void 0 ? 0 : _ref6$restMs,
    _ref6$buffer = _ref6.buffer,
    buffer = _ref6$buffer === void 0 ? 1 : _ref6$buffer,
    _ref6$debug = _ref6.debug,
    debug = _ref6$debug === void 0 ? null : _ref6$debug;
  var timeoutId;
  return function (_ref) {
    var x = _ref.x,
      y = _ref.y,
      placement = _ref.placement,
      refs = _ref.refs,
      onClose = _ref.onClose,
      nodeId = _ref.nodeId,
      tree = _ref.tree;
    return function onPointerMove(event) {
      var _refs$floating$curren;
      clearTimeout(timeoutId);
      if (event.pointerType && event.pointerType !== 'mouse') {
        return;
      }
      var target = event.target,
        clientX = event.clientX,
        clientY = event.clientY;
      var targetNode = target;
      if (event.type === 'pointermove' && isElement(refs.reference.current) && refs.reference.current.contains(targetNode) || (_refs$floating$curren = refs.floating.current) != null && _refs$floating$curren.contains(targetNode)) {
        return;
      } // If any child has a menu open, abort

      if (tree && getChildren(tree, nodeId).some(function (_ref2) {
        var context = _ref2.context;
        return context == null ? void 0 : context.open;
      })) {
        return;
      }
      if (!refs.reference.current || !refs.floating.current || placement == null || x == null || y == null) {
        return;
      }
      var refRect = refs.reference.current.getBoundingClientRect();
      var rect = refs.floating.current.getBoundingClientRect();
      var side = placement.split('-')[0];
      var cursorLeaveFromRight = x > rect.right - rect.width / 2;
      var cursorLeaveFromBottom = y > rect.bottom - rect.height / 2; // Within the rectangular trough between the two elements

      switch (side) {
        case 'top':
          if (clientX >= rect.left && clientX <= rect.right && clientY >= rect.bottom && clientY <= refRect.top) {
            return;
          }
          break;
        case 'bottom':
          if (clientX >= rect.left && clientX <= rect.right && clientY >= refRect.bottom && clientY <= rect.top) {
            return;
          }
          break;
        case 'left':
          if (clientX >= rect.right && clientX <= refRect.left && clientY >= rect.left && clientY <= rect.right) {
            return;
          }
          break;
        case 'right':
          if (clientX >= rect.right && clientX <= refRect.left && clientY >= rect.right && clientY <= refRect.left) {
            return;
          }
          break;
      }
      function getPolygon(_ref3) {
        var _ref7 = _slicedToArray(_ref3, 2),
          x = _ref7[0],
          y = _ref7[1];
        var isFloatingWider = rect.width > refRect.width;
        var isFloatingTaller = rect.height > refRect.height;
        switch (side) {
          case 'top':
            {
              var cursorPointOne = [isFloatingWider ? x : cursorLeaveFromRight ? x + buffer : x - buffer, y + buffer];
              var cursorPointTwo = [isFloatingWider ? x : cursorLeaveFromRight ? x - buffer : x + buffer, y + buffer];
              var commonPoints = [[rect.left, cursorLeaveFromRight ? rect.bottom - buffer : isFloatingWider ? rect.bottom - buffer : rect.top], [rect.right, cursorLeaveFromRight ? isFloatingWider ? rect.bottom - buffer : rect.top : rect.bottom - buffer]];
              if (cursorLeaveFromRight) {
                return [cursorPointOne, cursorPointTwo].concat(commonPoints);
              }
              return [cursorPointOne].concat(commonPoints, [cursorPointTwo]);
            }
          case 'bottom':
            {
              var _cursorPointOne = [isFloatingWider ? x : cursorLeaveFromRight ? x + buffer : x - buffer, y - buffer];
              var _cursorPointTwo = [isFloatingWider ? x : cursorLeaveFromRight ? x - buffer : x + buffer, y - buffer];
              var _commonPoints = [[rect.left, cursorLeaveFromRight ? rect.top + buffer : isFloatingWider ? rect.top + buffer : rect.bottom], [rect.right, cursorLeaveFromRight ? isFloatingWider ? rect.top + buffer : rect.bottom : rect.top + buffer]];
              if (cursorLeaveFromRight) {
                return [_cursorPointOne, _cursorPointTwo].concat(_commonPoints);
              }
              return [_cursorPointOne].concat(_commonPoints, [_cursorPointTwo]);
            }
          case 'left':
            {
              var _cursorPointOne2 = [x + buffer, isFloatingTaller ? y : cursorLeaveFromBottom ? y - buffer : y + buffer];
              var _cursorPointTwo2 = [x + buffer, isFloatingTaller ? y : cursorLeaveFromBottom ? y + buffer : y - buffer];
              var _commonPoints2 = [[cursorLeaveFromBottom ? rect.right - buffer : isFloatingTaller ? rect.right - buffer : rect.left, rect.top], [cursorLeaveFromBottom ? isFloatingTaller ? rect.right - buffer : rect.left : rect.right - buffer, rect.bottom]];
              if (cursorLeaveFromBottom) {
                return [_cursorPointOne2].concat(_commonPoints2, [_cursorPointTwo2]);
              }
              return [].concat(_commonPoints2, [_cursorPointOne2, _cursorPointTwo2]);
            }
          case 'right':
            {
              var _cursorPointOne3 = [x - buffer, isFloatingTaller ? y : cursorLeaveFromBottom ? y + buffer : y - buffer];
              var _cursorPointTwo3 = [x - buffer, isFloatingTaller ? y : cursorLeaveFromBottom ? y - buffer : y + buffer];
              var _commonPoints3 = [[cursorLeaveFromBottom ? rect.left + buffer : isFloatingTaller ? rect.left + buffer : rect.right, rect.top], [cursorLeaveFromBottom ? isFloatingTaller ? rect.left + buffer : rect.right : rect.left + buffer, rect.bottom]];
              if (cursorLeaveFromBottom) {
                return [_cursorPointOne3, _cursorPointTwo3].concat(_commonPoints3);
              }
              return [_cursorPointOne3].concat(_commonPoints3, [_cursorPointTwo3]);
            }
        }
      }
      var poly = getPolygon([x, y]);
      if (process.env.NODE_ENV !== "production") {
        debug == null ? void 0 : debug(poly.slice(0, 4).join(', '));
      }
      if (!pointInPolygon([clientX, clientY], poly)) {
        clearTimeout(timeoutId);
        onClose();
      } else if (restMs) {
        timeoutId = setTimeout(onClose, restMs);
      }
    };
  };
}
var DEFAULT_ID = 'floating-ui-root';
/**
 * Portals your floating element outside of the main app node.
 * @see https://floating-ui.com/docs/FloatingPortal
 */

var FloatingPortal = function FloatingPortal(_ref) {
  var children = _ref.children,
    _ref$id = _ref.id,
    id = _ref$id === void 0 ? DEFAULT_ID : _ref$id;
  var _useState = useState(false),
    _useState2 = _slicedToArray(_useState, 2),
    mounted = _useState2[0],
    setMounted = _useState2[1];
  var portalRef = useRef(null);
  index(function () {
    var root = document.getElementById(id);
    if (root) {
      portalRef.current = root;
    } else {
      portalRef.current = document.createElement('div');
      portalRef.current.id = id;
    }
    var el = portalRef.current;
    if (!document.body.contains(el)) {
      document.body.appendChild(el);
    }
    setMounted(true);
  }, [id]);
  if (mounted && portalRef.current) {
    return /*#__PURE__*/createPortal(children, portalRef.current);
  }
  return null;
};
function _extends() {
  _extends = Object.assign || function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];
      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }
    return target;
  };
  return _extends.apply(this, arguments);
}
var identifier = 'data-floating-ui-scroll-lock';
/**
 * Provides base styling for a fixed overlay element to dim content or block
 * pointer events behind a floating element.
 * It's a regular `<div>`, so it can be styled via any CSS solution you prefer.
 * @see https://floating-ui.com/docs/FloatingOverlay
 */

var FloatingOverlay = /*#__PURE__*/forwardRef(function FloatingOverlay(_ref, ref) {
  var _ref$lockScroll = _ref.lockScroll,
    lockScroll = _ref$lockScroll === void 0 ? false : _ref$lockScroll,
    rest = _objectWithoutProperties(_ref, _excluded);
  index(function () {
    if (!lockScroll) {
      return;
    }
    var scrollX = window.pageXOffset;
    var scrollY = window.pageYOffset; // RTL <body> scrollbar

    var scrollbarX = Math.round(document.documentElement.getBoundingClientRect().left) + document.documentElement.scrollLeft;
    var paddingProp = scrollbarX ? 'paddingLeft' : 'paddingRight';
    var scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
    var alreadyLocked = document.body.hasAttribute(identifier);
    if (alreadyLocked) {
      return;
    }
    Object.assign(document.body.style, _defineProperty({
      position: 'fixed',
      overflow: 'hidden',
      top: "-" + scrollY + "px",
      left: "-" + scrollX + "px",
      right: '0'
    }, paddingProp, scrollbarWidth + "px"));
    document.body.setAttribute(identifier, '');
    return function () {
      Object.assign(document.body.style, _defineProperty({
        position: '',
        overflow: '',
        top: '',
        left: '',
        right: ''
      }, paddingProp, ''));
      document.body.removeAttribute(identifier);
      window.scrollTo(scrollX, scrollY);
    };
  }, [lockScroll]);
  return /*#__PURE__*/React__default.createElement("div", _extends({
    ref: ref
  }, rest, {
    style: _objectSpread({
      position: 'fixed',
      overflow: 'auto',
      top: 0,
      right: 0,
      bottom: 0,
      left: 0
    }, rest.style)
  }));
});
function stopEvent(event) {
  event.preventDefault();
  event.stopPropagation();
}
function useLatestRef(value) {
  var ref = useRef(value);
  index(function () {
    ref.current = value;
  });
  return ref;
}
function focus$1(el) {
  requestAnimationFrame(function () {
    el == null ? void 0 : el.focus();
  });
}
var SELECTOR = "input:not([type='hidden']):not([disabled]),select:not([disabled])," + 'textarea:not([disabled]),a[href],button:not([disabled]),[tabindex],' + 'iframe,object,embed,area[href],audio[controls],video[controls],' + "[contenteditable]:not([contenteditable='false'])";
var FocusGuard = /*#__PURE__*/forwardRef(function FocusGuard(props, ref) {
  return /*#__PURE__*/React__default.createElement("span", _extends({}, props, {
    ref: ref,
    tabIndex: 0,
    style: {
      position: 'fixed',
      opacity: '0',
      pointerEvents: 'none',
      outline: '0'
    }
  }));
});

/**
 * Provides focus management for the floating element.
 * @see https://floating-ui.com/docs/FloatingFocusManager
 */
function FloatingFocusManager(_ref) {
  var _ref$context = _ref.context,
    refs = _ref$context.refs,
    nodeId = _ref$context.nodeId,
    onOpenChange = _ref$context.onOpenChange,
    children = _ref.children,
    _ref$order = _ref.order,
    order = _ref$order === void 0 ? ['content'] : _ref$order,
    _ref$endGuard = _ref.endGuard,
    endGuard = _ref$endGuard === void 0 ? true : _ref$endGuard,
    _ref$preventTabbing = _ref.preventTabbing,
    preventTabbing = _ref$preventTabbing === void 0 ? false : _ref$preventTabbing,
    _ref$initialFocus = _ref.initialFocus,
    initialFocus = _ref$initialFocus === void 0 ? 0 : _ref$initialFocus,
    _ref$returnFocus = _ref.returnFocus,
    returnFocus = _ref$returnFocus === void 0 ? true : _ref$returnFocus,
    _ref$modal = _ref.modal,
    modal = _ref$modal === void 0 ? true : _ref$modal;
  var orderRef = useLatestRef(order);
  var onOpenChangeRef = useLatestRef(onOpenChange);
  var tree = useFloatingTree();
  var getTabbableElements = useCallback(function () {
    return orderRef.current.map(function (type) {
      if (isHTMLElement(refs.reference.current) && type === 'reference') {
        return refs.reference.current;
      }
      if (refs.floating.current && type === 'floating') {
        return refs.floating.current;
      }
      if (type === 'content') {
        var _refs$floating$curren, _refs$floating$curren2;
        return Array.from((_refs$floating$curren = (_refs$floating$curren2 = refs.floating.current) == null ? void 0 : _refs$floating$curren2.querySelectorAll(SELECTOR)) != null ? _refs$floating$curren : []);
      }
      return null;
    }).flat().filter(function (el) {
      if (el === refs.floating.current || el === refs.reference.current) {
        return true;
      }
      if (isHTMLElement(el)) {
        var _el$getAttribute;
        var tabIndex = (_el$getAttribute = el.getAttribute('tabindex')) != null ? _el$getAttribute : '0';
        return tabIndex[0].trim() !== '-';
      }
    });
  }, [orderRef, refs.floating, refs.reference]);
  useEffect(function () {
    // If the floating element has no focusable elements inside it, fallback
    // to focusing the floating element and preventing tab navigation
    var noTabbableContentElements = getTabbableElements().filter(function (el) {
      return el !== refs.floating.current &&
      // @ts-expect-error
      el !== refs.reference.current;
    }).length === 0;
    function onKeyDown(event) {
      if (event.key === 'Tab') {
        if (preventTabbing || noTabbableContentElements) {
          stopEvent(event);
        }
        var els = getTabbableElements();
        if (orderRef.current[0] === 'reference' && event.target === refs.reference.current) {
          stopEvent(event);
          if (event.shiftKey) {
            focus$1(els[els.length - 1]);
          } else {
            focus$1(els[1]);
          }
        }
        if (orderRef.current[1] === 'floating' && event.target === refs.floating.current && event.shiftKey) {
          stopEvent(event);
          focus$1(els[0]);
        }
      }
    }
    var doc = getDocument(refs.floating.current);
    doc.addEventListener('keydown', onKeyDown);
    return function () {
      doc.removeEventListener('keydown', onKeyDown);
    };
  }, [preventTabbing, getTabbableElements, orderRef, refs.floating, refs.reference]);
  useEffect(function () {
    function onFloatingFocusOut(event) {
      var _refs$floating$curren3;
      var target = event.relatedTarget;
      if (!((_refs$floating$curren3 = refs.floating.current) != null && _refs$floating$curren3.contains(target)) && isElement(refs.reference.current) && !refs.reference.current.contains(target) && !(tree && getChildren(tree, nodeId).some(function (child) {
        var _child$context, _child$context$refs$f;
        return (_child$context = child.context) == null ? void 0 : (_child$context$refs$f = _child$context.refs.floating.current) == null ? void 0 : _child$context$refs$f.contains(target);
      }))) {
        onOpenChangeRef.current(false);
      }
    }
    var floating = refs.floating.current;
    var reference = refs.reference.current;
    if (floating && isHTMLElement(reference)) {
      !modal && floating.addEventListener('focusout', onFloatingFocusOut);
      var cleanup = modal ? hideOthers(floating) : null;
      return function () {
        !modal && floating.removeEventListener('focusout', onFloatingFocusOut);
        cleanup == null ? void 0 : cleanup();
      };
    }
  }, [nodeId, tree, modal, onOpenChangeRef, getTabbableElements, initialFocus, refs.floating, refs.reference]);
  useEffect(function () {
    var _getDocument$activeEl;
    if (preventTabbing) {
      return;
    }
    var floating = refs.floating.current;
    var previouslyFocusedElement = (_getDocument$activeEl = getDocument(floating).activeElement) != null ? _getDocument$activeEl : document.activeElement;
    if (typeof initialFocus === 'number') {
      var _getTabbableElements$;
      focus$1((_getTabbableElements$ = getTabbableElements()[initialFocus]) != null ? _getTabbableElements$ : floating);
    } else if (isHTMLElement(initialFocus == null ? void 0 : initialFocus.current)) {
      var _initialFocus$current;
      focus$1((_initialFocus$current = initialFocus.current) != null ? _initialFocus$current : floating);
    }
    return function () {
      if (returnFocus && isHTMLElement(previouslyFocusedElement)) {
        focus$1(previouslyFocusedElement);
      }
    };
  }, [preventTabbing, getTabbableElements, initialFocus, modal, returnFocus, refs.floating]);
  return /*#__PURE__*/React__default.createElement(React__default.Fragment, null, modal && /*#__PURE__*/React__default.createElement(FocusGuard, {
    onFocus: function onFocus(event) {
      stopEvent(event);
      var els = getTabbableElements();
      if (order[0] === 'reference') {
        focus$1(els[0]);
      } else {
        focus$1(els[els.length - 1]);
      }
    }
  }), /*#__PURE__*/cloneElement(children, order.includes('floating') ? {
    tabIndex: 0
  } : {}), modal && endGuard && /*#__PURE__*/React__default.createElement(FocusGuard, {
    onFocus: function onFocus(event) {
      stopEvent(event);
      focus$1(getTabbableElements()[0]);
    }
  }));
}
function getDelay(value, prop, pointerType) {
  if (pointerType && pointerType !== 'mouse') {
    return 0;
  }
  if (typeof value === 'number') {
    return value;
  }
  return value == null ? void 0 : value[prop];
}

/**
 * Adds hover event listeners that change the open state, like CSS :hover.
 * @see https://floating-ui.com/docs/useHover
 */
var useHover = function useHover(context, _temp) {
  var _ref8 = _temp === void 0 ? {} : _temp,
    _ref8$enabled = _ref8.enabled,
    enabled = _ref8$enabled === void 0 ? true : _ref8$enabled,
    _ref8$delay = _ref8.delay,
    delay = _ref8$delay === void 0 ? 0 : _ref8$delay,
    _ref8$handleClose = _ref8.handleClose,
    handleClose = _ref8$handleClose === void 0 ? null : _ref8$handleClose,
    _ref8$mouseOnly = _ref8.mouseOnly,
    mouseOnly = _ref8$mouseOnly === void 0 ? false : _ref8$mouseOnly,
    _ref8$restMs = _ref8.restMs,
    restMs = _ref8$restMs === void 0 ? 0 : _ref8$restMs;
  var open = context.open,
    onOpenChange = context.onOpenChange,
    dataRef = context.dataRef,
    events = context.events,
    refs = context.refs;
  var tree = useFloatingTree();
  var onOpenChangeRef = useLatestRef(onOpenChange);
  var handleCloseRef = useLatestRef(handleClose);
  var pointerTypeRef = useRef();
  var timeoutRef = useRef();
  var handlerRef = useRef();
  var restTimeoutRef = useRef();
  var blockMouseMoveRef = useRef(true);
  index(function () {
    if (!enabled) {
      return;
    }
    if (!open) {
      pointerTypeRef.current = undefined;
    }
  });
  useEffect(function () {
    if (!enabled) {
      return;
    }
    function onDismiss() {
      clearTimeout(timeoutRef.current);
      clearTimeout(restTimeoutRef.current);
      blockMouseMoveRef.current = true;
    }
    events.on('dismiss', onDismiss);
    return function () {
      events.off('dismiss', onDismiss);
    };
  }, [enabled, events, refs.floating]);
  useEffect(function () {
    if (!enabled || !handleCloseRef.current) {
      return;
    }
    function onLeave() {
      var _dataRef$current$open;
      if ((_dataRef$current$open = dataRef.current.openEvent) != null && _dataRef$current$open.type.includes('mouse')) {
        onOpenChangeRef.current(false);
      }
    }
    var html = getDocument(refs.floating.current).documentElement;
    html.addEventListener('mouseleave', onLeave);
    return function () {
      html.removeEventListener('mouseleave', onLeave);
    };
  }, [refs.floating, onOpenChangeRef, enabled, handleCloseRef, dataRef]);
  var closeWithDelay = useCallback(function (runElseBranch) {
    if (runElseBranch === void 0) {
      runElseBranch = true;
    }
    if (delay && !handlerRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = setTimeout(function () {
        return onOpenChangeRef.current(false);
      }, getDelay(delay, 'close', pointerTypeRef.current));
    } else if (runElseBranch) {
      onOpenChangeRef.current(false);
    }
  }, [delay, onOpenChangeRef]);
  var cleanupPointerMoveHandler = useCallback(function () {
    if (handlerRef.current) {
      getDocument(refs.floating.current).removeEventListener('pointermove', handlerRef.current);
      handlerRef.current = undefined;
    }
  }, [refs.floating]);
  useEffect(function () {
    if (!open) {
      cleanupPointerMoveHandler();
    }
  }, [open, enabled, cleanupPointerMoveHandler]); // Registering the mouse events on the reference directly to bypass React's
  // delegation system. If the cursor was on a disabled element and then entered
  // the reference (no gap), `mouseenter` doesn't fire in the delegation system.

  useEffect(function () {
    if (!enabled) {
      return;
    }
    function onMouseEnter(event) {
      clearTimeout(timeoutRef.current);
      blockMouseMoveRef.current = false;
      if (open || mouseOnly && pointerTypeRef.current !== 'mouse' || restMs > 0 && getDelay(delay, 'open') === 0) {
        return;
      }
      dataRef.current.openEvent = event;
      if (delay) {
        timeoutRef.current = setTimeout(function () {
          onOpenChangeRef.current(true);
        }, getDelay(delay, 'open', pointerTypeRef.current));
      } else {
        onOpenChangeRef.current(true);
      }
    }
    function onMouseLeave(event) {
      var _dataRef$current$open2, _dataRef$current$open3;
      if (((_dataRef$current$open2 = dataRef.current.openEvent) == null ? void 0 : _dataRef$current$open2.type) === 'click' || ((_dataRef$current$open3 = dataRef.current.openEvent) == null ? void 0 : _dataRef$current$open3.type) === 'pointerdown') {
        return;
      }
      var doc = getDocument(refs.floating.current);
      clearTimeout(restTimeoutRef.current);
      if (handleCloseRef.current) {
        clearTimeout(timeoutRef.current);
        handlerRef.current && doc.removeEventListener('pointermove', handlerRef.current);
        handlerRef.current = handleCloseRef.current(_objectSpread(_objectSpread({}, context), {}, {
          tree: tree,
          x: event.clientX,
          y: event.clientY,
          onClose: function onClose() {
            cleanupPointerMoveHandler();
            closeWithDelay();
          }
        }));
        doc.addEventListener('pointermove', handlerRef.current);
        return;
      }
      closeWithDelay();
    }
    var reference = refs.reference.current;
    if (isElement(reference)) {
      reference.addEventListener('mouseenter', onMouseEnter);
      reference.addEventListener('mouseleave', onMouseLeave);
      return function () {
        reference.removeEventListener('mouseenter', onMouseEnter);
        reference.removeEventListener('mouseleave', onMouseLeave);
      };
    }
  }, [enabled, closeWithDelay, context, delay, handleCloseRef, dataRef, mouseOnly, onOpenChangeRef, open, tree, restMs, cleanupPointerMoveHandler, refs.reference, refs.floating]);
  if (!enabled) {
    return {};
  }
  function setPointerRef(event) {
    pointerTypeRef.current = event.pointerType;
  }
  return {
    reference: {
      onPointerDown: setPointerRef,
      onPointerEnter: setPointerRef,
      onMouseMove: function onMouseMove() {
        if (open || restMs === 0) {
          return;
        }
        clearTimeout(restTimeoutRef.current);
        restTimeoutRef.current = setTimeout(function () {
          if (!blockMouseMoveRef.current) {
            onOpenChange(true);
          }
        }, restMs);
      }
    },
    floating: {
      onMouseEnter: function onMouseEnter() {
        clearTimeout(timeoutRef.current);
      },
      onMouseLeave: function onMouseLeave() {
        return closeWithDelay(false);
      }
    }
  };
};
var FloatingDelayGroupContext = /*#__PURE__*/createContext({
  delay: 1000,
  initialDelay: 1000,
  currentId: null,
  setCurrentId: function setCurrentId() {},
  setState: function setState() {}
});
var useDelayGroupContext = function useDelayGroupContext() {
  return useContext(FloatingDelayGroupContext);
};
/**
 * Provides context for a group of floating elements that should share a
 * `delay`.
 * @see https://floating-ui.com/docs/FloatingDelayGroup
 */

var FloatingDelayGroup = function FloatingDelayGroup(_ref) {
  var children = _ref.children,
    delay = _ref.delay;
  var _useState3 = useState({
      delay: delay,
      initialDelay: delay,
      currentId: null
    }),
    _useState4 = _slicedToArray(_useState3, 2),
    state = _useState4[0],
    setState = _useState4[1];
  var setCurrentId = useCallback(function (currentId) {
    setState(function (state) {
      return _objectSpread(_objectSpread({}, state), {}, {
        currentId: currentId
      });
    });
  }, []);
  return /*#__PURE__*/React__default.createElement(FloatingDelayGroupContext.Provider, {
    value: useMemo(function () {
      return _objectSpread(_objectSpread({}, state), {}, {
        setState: setState,
        setCurrentId: setCurrentId
      });
    }, [state, setState, setCurrentId])
  }, children);
};
var useDelayGroup = function useDelayGroup(_ref2, _ref3) {
  var open = _ref2.open,
    onOpenChange = _ref2.onOpenChange;
  var id = _ref3.id;
  var _useDelayGroupContext = useDelayGroupContext(),
    currentId = _useDelayGroupContext.currentId,
    initialDelay = _useDelayGroupContext.initialDelay,
    setState = _useDelayGroupContext.setState;
  var onOpenChangeRef = useLatestRef(onOpenChange);
  useEffect(function () {
    if (currentId && onOpenChangeRef.current) {
      setState(function (state) {
        return _objectSpread(_objectSpread({}, state), {}, {
          delay: {
            open: 1,
            close: getDelay(initialDelay, 'close')
          }
        });
      });
      if (currentId !== id) {
        onOpenChangeRef.current(false);
      }
    }
  }, [id, onOpenChangeRef, setState, currentId, initialDelay]);
  useEffect(function () {
    if (!open && currentId === id && onOpenChangeRef.current) {
      onOpenChangeRef.current(false);
      setState(function (state) {
        return _objectSpread(_objectSpread({}, state), {}, {
          delay: initialDelay,
          currentId: null
        });
      });
    }
  }, [open, setState, currentId, id, onOpenChangeRef, initialDelay]);
};

/**
 * Adds relevant screen reader props for a given element `role`.
 * @see https://floating-ui.com/docs/useRole
 */
var useRole = function useRole(_ref, _temp) {
  var open = _ref.open;
  var _ref9 = _temp === void 0 ? {} : _temp,
    _ref9$enabled = _ref9.enabled,
    enabled = _ref9$enabled === void 0 ? true : _ref9$enabled,
    _ref9$role = _ref9.role,
    role = _ref9$role === void 0 ? 'dialog' : _ref9$role;
  var rootId = useId();
  var referenceId = useId();
  var floatingProps = {
    id: rootId,
    role: role
  };
  if (!enabled) {
    return {};
  }
  if (role === 'tooltip') {
    return {
      reference: {
        'aria-describedby': open ? rootId : undefined
      },
      floating: floatingProps
    };
  }
  return {
    reference: _objectSpread(_objectSpread({
      'aria-expanded': open ? 'true' : 'false',
      'aria-haspopup': role,
      'aria-controls': open ? rootId : undefined
    }, role === 'listbox' && {
      role: 'combobox'
    }), role === 'menu' && {
      id: referenceId
    }),
    floating: _objectSpread(_objectSpread({}, floatingProps), role === 'menu' && {
      'aria-labelledby': referenceId
    })
  };
};

/**
 * Adds click event listeners that change the open state (toggle behavior).
 * @see https://floating-ui.com/docs/useClick
 */
var useClick = function useClick(_ref, _temp) {
  var open = _ref.open,
    onOpenChange = _ref.onOpenChange,
    dataRef = _ref.dataRef;
  var _ref10 = _temp === void 0 ? {} : _temp,
    _ref10$enabled = _ref10.enabled,
    enabled = _ref10$enabled === void 0 ? true : _ref10$enabled,
    _ref10$pointerDown = _ref10.pointerDown,
    pointerDown = _ref10$pointerDown === void 0 ? false : _ref10$pointerDown,
    _ref10$toggle = _ref10.toggle,
    toggle = _ref10$toggle === void 0 ? true : _ref10$toggle;
  var pointerTypeRef = useRef();
  if (!enabled) {
    return {};
  }
  return {
    reference: _objectSpread(_objectSpread({}, pointerDown && {
      onPointerDown: function onPointerDown(event) {
        pointerTypeRef.current = event.pointerType;
        if (open) {
          var _dataRef$current$open;
          if (toggle && ((_dataRef$current$open = dataRef.current.openEvent) == null ? void 0 : _dataRef$current$open.type) === 'pointerdown') {
            onOpenChange(false);
          }
        } else {
          onOpenChange(true);
        }
        dataRef.current.openEvent = event.nativeEvent;
      }
    }), {}, {
      onClick: function onClick(event) {
        if (pointerDown && pointerTypeRef.current) {
          pointerTypeRef.current = undefined;
          return;
        }
        if (open) {
          var _dataRef$current$open2;
          if (toggle && ((_dataRef$current$open2 = dataRef.current.openEvent) == null ? void 0 : _dataRef$current$open2.type) === 'click') {
            onOpenChange(false);
          }
        } else {
          onOpenChange(true);
        }
        dataRef.current.openEvent = event.nativeEvent;
      }
    })
  };
};

/**
 * Adds listeners that dismiss (close) the floating element.
 * @see https://floating-ui.com/docs/useDismiss
 */
var useDismiss = function useDismiss(_ref, _temp) {
  var open = _ref.open,
    onOpenChange = _ref.onOpenChange,
    refs = _ref.refs,
    events = _ref.events,
    nodeId = _ref.nodeId;
  var _ref11 = _temp === void 0 ? {} : _temp,
    _ref11$enabled = _ref11.enabled,
    enabled = _ref11$enabled === void 0 ? true : _ref11$enabled,
    _ref11$escapeKey = _ref11.escapeKey,
    escapeKey = _ref11$escapeKey === void 0 ? true : _ref11$escapeKey,
    _ref11$outsidePointer = _ref11.outsidePointerDown,
    outsidePointerDown = _ref11$outsidePointer === void 0 ? true : _ref11$outsidePointer,
    _ref11$referencePoint = _ref11.referencePointerDown,
    referencePointerDown = _ref11$referencePoint === void 0 ? false : _ref11$referencePoint,
    _ref11$ancestorScroll = _ref11.ancestorScroll,
    ancestorScroll = _ref11$ancestorScroll === void 0 ? false : _ref11$ancestorScroll,
    _ref11$bubbles = _ref11.bubbles,
    bubbles = _ref11$bubbles === void 0 ? true : _ref11$bubbles;
  var tree = useFloatingTree();
  var onOpenChangeRef = useLatestRef(onOpenChange);
  var isFocusInsideFloating = useCallback(function () {
    var _refs$floating$curren;
    return (_refs$floating$curren = refs.floating.current) == null ? void 0 : _refs$floating$curren.contains(getDocument(refs.floating.current).activeElement);
  }, [refs.floating]);
  var focusReference = useCallback(function () {
    if (isHTMLElement(refs.reference.current)) {
      refs.reference.current.focus();
    }
  }, [refs.reference]);
  useEffect(function () {
    if (!open || !enabled) {
      return;
    }
    function onKeyDown(event) {
      if (event.key === 'Escape') {
        if (!bubbles && !isFocusInsideFloating()) {
          return;
        }
        events.emit('dismiss');
        onOpenChangeRef.current(false);
        focusReference();
      }
    }
    function onPointerDown(event) {
      var _refs$floating$curren2;
      var targetIsInsideChildren = tree && getChildren(tree, nodeId).some(function (node) {
        var _node$context, _node$context$refs$fl;
        return (_node$context = node.context) == null ? void 0 : (_node$context$refs$fl = _node$context.refs.floating.current) == null ? void 0 : _node$context$refs$fl.contains(event.target);
      });
      if ((_refs$floating$curren2 = refs.floating.current) != null && _refs$floating$curren2.contains(event.target) || isElement(refs.reference.current) && refs.reference.current.contains(event.target) || targetIsInsideChildren) {
        return;
      }
      if (!bubbles && !isFocusInsideFloating()) {
        return;
      }
      events.emit('dismiss');
      onOpenChangeRef.current(false);
      focusReference();
    }
    function onScroll() {
      onOpenChangeRef.current(false);
    }
    var doc = getDocument(refs.floating.current);
    escapeKey && doc.addEventListener('keydown', onKeyDown); // Use `mousedown` instead of `pointerdown` as it acts more like a click
    // on touch devices than a `touchstart` (which can result in accidental
    // dismissals too easily.)

    outsidePointerDown && doc.addEventListener('mousedown', onPointerDown);
    var ancestors = (ancestorScroll ? [].concat(_toConsumableArray(isElement(refs.reference.current) ? getOverflowAncestors(refs.reference.current) : []), _toConsumableArray(isElement(refs.floating.current) ? getOverflowAncestors(refs.floating.current) : [])) : []).filter(function (ancestor) {
      var _doc$defaultView;
      return (
        // Ignore the visual viewport for scrolling dismissal (allow pinch-zoom)
        ancestor !== ((_doc$defaultView = doc.defaultView) == null ? void 0 : _doc$defaultView.visualViewport)
      );
    });
    ancestors.forEach(function (ancestor) {
      return ancestor.addEventListener('scroll', onScroll, {
        passive: true
      });
    });
    return function () {
      escapeKey && doc.removeEventListener('keydown', onKeyDown);
      outsidePointerDown && doc.removeEventListener('mousedown', onPointerDown);
      ancestors.forEach(function (ancestor) {
        return ancestor.removeEventListener('scroll', onScroll);
      });
    };
  }, [escapeKey, outsidePointerDown, events, tree, nodeId, open, onOpenChangeRef, focusReference, ancestorScroll, enabled, bubbles, isFocusInsideFloating, refs.floating, refs.reference]);
  if (!enabled) {
    return {};
  }
  return {
    reference: {
      onPointerDown: function onPointerDown() {
        if (referencePointerDown) {
          events.emit('dismiss');
          onOpenChange(false);
        }
      }
    }
  };
};

/**
 * Adds focus event listeners that change the open state, like CSS :focus.
 * @see https://floating-ui.com/docs/useFocus
 */
var useFocus = function useFocus(_ref, _temp) {
  var open = _ref.open,
    onOpenChange = _ref.onOpenChange,
    dataRef = _ref.dataRef,
    refs = _ref.refs,
    events = _ref.events;
  var _ref12 = _temp === void 0 ? {} : _temp,
    _ref12$enabled = _ref12.enabled,
    enabled = _ref12$enabled === void 0 ? true : _ref12$enabled,
    _ref12$keyboardOnly = _ref12.keyboardOnly,
    keyboardOnly = _ref12$keyboardOnly === void 0 ? true : _ref12$keyboardOnly;
  var blockFocusRef = useRef(false);
  useEffect(function () {
    var _doc$defaultView;
    if (!enabled) {
      return;
    }
    var doc = getDocument(refs.floating.current);
    var win = (_doc$defaultView = doc.defaultView) != null ? _doc$defaultView : window;
    function onBlur() {
      blockFocusRef.current = !open;
    }
    function onFocus() {
      setTimeout(function () {
        blockFocusRef.current = false;
      });
    }
    win.addEventListener('focus', onFocus);
    win.addEventListener('blur', onBlur);
    return function () {
      win.removeEventListener('focus', onFocus);
      win.removeEventListener('blur', onBlur);
    };
  }, [refs.floating, open, enabled]);
  useEffect(function () {
    if (!enabled) {
      return;
    }
    function onDismiss() {
      blockFocusRef.current = true;
    }
    events.on('dismiss', onDismiss);
    return function () {
      events.off('dismiss', onDismiss);
    };
  }, [events, enabled]);
  if (!enabled) {
    return {};
  }
  return {
    reference: {
      onPointerDown: function onPointerDown(_ref2) {
        var pointerType = _ref2.pointerType;
        blockFocusRef.current = !!(pointerType && keyboardOnly);
      },
      onFocus: function onFocus(event) {
        var _dataRef$current$open, _refs$reference$curre, _dataRef$current$open2;

        // Note: due to the `window` focus/blur listeners, switching between
        // DevTools touch/normal mode may cause this to fail on the first
        // focus of the window/touch of the element as it gets set to `false`.
        if (blockFocusRef.current) {
          return;
        } // Dismiss with click should ignore the subsequent `focus` trigger, but
        // only if the click originated inside the reference element.

        if (event.type === 'focus' && ((_dataRef$current$open = dataRef.current.openEvent) == null ? void 0 : _dataRef$current$open.type) === 'mousedown' && isElement(refs.reference.current) && (_refs$reference$curre = refs.reference.current) != null && _refs$reference$curre.contains((_dataRef$current$open2 = dataRef.current.openEvent) == null ? void 0 : _dataRef$current$open2.target)) {
          return;
        }
        dataRef.current.openEvent = event.nativeEvent;
        onOpenChange(true);
      },
      onBlur: function onBlur(event) {
        var _refs$floating$curren;
        var target = event.relatedTarget; // When focusing the reference element (e.g. regular click), then
        // clicking into the floating element, prevent it from hiding.
        // Note: it must be focusable, e.g. `tabindex="-1"`.

        if ((_refs$floating$curren = refs.floating.current) != null && _refs$floating$curren.contains(target) || isElement(refs.reference.current) && refs.reference.current.contains(target)) {
          return;
        }
        blockFocusRef.current = false;
        onOpenChange(false);
      }
    }
  };
};
var FOCUSABLE_ELEMENT_SELECTOR = 'a[href],area[href],input:not([disabled]),select:not([disabled]),textarea:not([disabled]),button:not([disabled]),iframe,object,embed,*[tabindex],*[contenteditable]';
var DEFAULT_ORDER = ['content'];

// When working with nested elements, we need to let the rendering occur before
// attempting focus. This fixes unwanted scrolling to the bottom of the document
function focus(el) {
  requestAnimationFrame(function () {
    el == null ? void 0 : el.focus();
  });
}
/**
 * @deprecated Use `<FloatingFocusManager />` instead.
 */

var useFocusTrap = function useFocusTrap(_ref, _temp) {
  var open = _ref.open,
    onOpenChange = _ref.onOpenChange,
    refs = _ref.refs,
    nodeId = _ref.nodeId;
  var _ref13 = _temp === void 0 ? {} : _temp,
    _ref13$enabled = _ref13.enabled,
    enabled = _ref13$enabled === void 0 ? true : _ref13$enabled,
    _ref13$initialContent = _ref13.initialContentFocus,
    initialContentFocus = _ref13$initialContent === void 0 ? 0 : _ref13$initialContent,
    _ref13$order = _ref13.order,
    order = _ref13$order === void 0 ? DEFAULT_ORDER : _ref13$order,
    _ref13$modal = _ref13.modal,
    modal = _ref13$modal === void 0 ? true : _ref13$modal,
    _ref13$inert = _ref13.inert,
    inert = _ref13$inert === void 0 ? false : _ref13$inert;
  var initializedRef = useRef(false);
  var beforeRef = useRef(null);
  var afterRef = useRef(null);
  var modalRef = useLatestRef(modal);
  var orderRef = useLatestRef(order);
  if (process.env.NODE_ENV !== "production") {
    if (modal && order.includes('reference')) {
      console.warn(['Floating UI: useFocusTrap() `order` array cannot contain', '"reference" while in `modal` mode.'].join(' '));
    }
  }
  var getFocusableElements = useCallback(function () {
    return orderRef.current.map(function (type) {
      if (isHTMLElement(refs.reference.current) && type === 'reference') {
        return refs.reference.current;
      }
      if (refs.floating.current && type === 'floating') {
        return refs.floating.current;
      }
      if (type === 'content') {
        var _refs$floating$curren, _refs$floating$curren2;
        return Array.from((_refs$floating$curren = (_refs$floating$curren2 = refs.floating.current) == null ? void 0 : _refs$floating$curren2.querySelectorAll(FOCUSABLE_ELEMENT_SELECTOR)) != null ? _refs$floating$curren : []);
      }
      return null;
    }).filter(Boolean).flat(); // Ignore `order` dep; only respond to changes on `open`
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refs.floating, refs.reference]); // Focus guard elements
  // https://github.com/w3c/aria-practices/issues/545

  index(function () {
    var floating = refs.floating.current;
    if (!enabled || !open || !floating || !modal) {
      return;
    }
    function createFocusGuardElement() {
      var doc = getDocument(floating);
      var el = doc.createElement('div');
      el.tabIndex = 0;
      Object.assign(el.style, {
        position: 'fixed',
        outline: '0',
        pointerEvents: 'none'
      });
      el.setAttribute('aria-hidden', 'true');
      return el;
    }
    if (!beforeRef.current) {
      beforeRef.current = createFocusGuardElement();
    }
    if (!afterRef.current) {
      afterRef.current = createFocusGuardElement();
    }
    var before = beforeRef.current;
    var after = afterRef.current;
    floating.insertAdjacentElement('beforebegin', before);
    floating.insertAdjacentElement('afterend', after);
    function onFocus(event) {
      var _focusableElements;
      stopEvent(event);
      var focusableElements = getFocusableElements();
      (_focusableElements = focusableElements[event.target === after ? 0 : focusableElements.length - 1]) == null ? void 0 : _focusableElements.focus();
    }
    before.addEventListener('focus', onFocus);
    after.addEventListener('focus', onFocus);
    return function () {
      var _before$parentNode, _after$parentNode;
      before.removeEventListener('focus', onFocus);
      after.removeEventListener('focus', onFocus);
      if ((_before$parentNode = before.parentNode) != null && _before$parentNode.contains(before)) {
        before.parentNode.removeChild(before);
      }
      if ((_after$parentNode = after.parentNode) != null && _after$parentNode.contains(after)) {
        after.parentNode.removeChild(after);
      }
    };
  }, [enabled, open, modal, inert, getFocusableElements, refs.floating]); // Inert

  useEffect(function () {
    if (!enabled || !open || !inert) {
      return;
    }
    function onKeyDown(event) {
      if (event.key === 'Tab') {
        stopEvent(event);
      }
    }
    var doc = getDocument(refs.floating.current);
    doc.addEventListener('keydown', onKeyDown);
    return function () {
      doc.removeEventListener('keydown', onKeyDown);
    };
  }, [enabled, open, inert, refs.floating]); // Initial focus

  useEffect(function () {
    if (!enabled) {
      return;
    }
    var focusableElements = getFocusableElements();
    if (inert) {
      if (open) {
        var first = focusableElements[0];
        if (first === refs.floating.current && !first.contains(first.ownerDocument.activeElement)) {
          focus(first);
        }
      }
      return;
    }
    if (open) {
      if (typeof initialContentFocus === 'number') {
        focus(focusableElements[initialContentFocus]);
      } else if (initialContentFocus.current) {
        focus(focusableElements.find(function (element) {
          return element === initialContentFocus.current;
        }));
      }
    }
  }, [getFocusableElements, open, inert, modal, initialContentFocus, enabled, refs.reference, refs.floating]); // Return focus to reference

  useEffect(function () {
    if (!enabled || inert) {
      return;
    }
    if (!open && modalRef.current && initializedRef.current && isHTMLElement(refs.reference.current)) {
      focus(refs.reference.current);
    }
  }, [open, enabled, inert, modalRef, refs.reference]); // Hide all outside nodes from screen readers

  useEffect(function () {
    if (!open || !modal || !enabled || !refs.floating.current) {
      return;
    }
    return hideOthers(refs.floating.current);
  }, [open, modal, enabled, refs.floating]);
  useEffect(function () {
    initializedRef.current = true;
    return function () {
      initializedRef.current = false;
    };
  }, []);
  var tree = useFloatingTree();
  function onBlur(event) {
    var _refs$floating$curren3;
    var target = event.relatedTarget;
    if (!((_refs$floating$curren3 = refs.floating.current) != null && _refs$floating$curren3.contains(target)) && isElement(refs.reference.current) && !refs.reference.current.contains(target) && !(tree && getChildren(tree, nodeId).some(function (child) {
      var _child$context, _child$context$refs$f, _child$context$refs$f2;
      return (_child$context = child.context) == null ? void 0 : (_child$context$refs$f = _child$context.refs.floating) == null ? void 0 : (_child$context$refs$f2 = _child$context$refs$f.current) == null ? void 0 : _child$context$refs$f2.contains(target);
    }))) {
      onOpenChange(false);
    }
  }
  if (!enabled) {
    return {};
  }
  if (modal) {
    return {
      floating: {
        'aria-modal': 'true'
      }
    };
  }
  return {
    reference: {
      onBlur: onBlur
    },
    floating: {
      onBlur: onBlur
    }
  };
};
function usePrevious(value) {
  var ref = useRef();
  index(function () {
    ref.current = value;
  }, [value]);
  return ref.current;
}
var ARROW_UP = 'ArrowUp';
var ARROW_DOWN = 'ArrowDown';
var ARROW_LEFT = 'ArrowLeft';
var ARROW_RIGHT = 'ArrowRight';
function findNonDisabledIndex(listRef, _temp) {
  var _ref14 = _temp === void 0 ? {} : _temp,
    _ref14$startingIndex = _ref14.startingIndex,
    startingIndex = _ref14$startingIndex === void 0 ? -1 : _ref14$startingIndex,
    _ref14$decrement = _ref14.decrement,
    decrement = _ref14$decrement === void 0 ? false : _ref14$decrement;
  var list = listRef.current;
  var index = startingIndex;
  do {
    var _list$index, _list$index2;
    index = index + (decrement ? -1 : 1);
  } while (index >= 0 && index <= list.length - 1 && (list[index] == null || (_list$index = list[index]) != null && _list$index.hasAttribute('disabled') || ((_list$index2 = list[index]) == null ? void 0 : _list$index2.getAttribute('aria-disabled')) === 'true'));
  return index;
}
function doSwitch(orientation, vertical, horizontal) {
  switch (orientation) {
    case 'vertical':
      return vertical;
    case 'horizontal':
      return horizontal;
    default:
      return vertical || horizontal;
  }
}
function isMainOrientationKey(key, orientation) {
  var vertical = key === ARROW_UP || key === ARROW_DOWN;
  var horizontal = key === ARROW_LEFT || key === ARROW_RIGHT;
  return doSwitch(orientation, vertical, horizontal);
}
function isMainOrientationToStartKey(key, orientation, rtl) {
  var vertical = key === ARROW_UP;
  var horizontal = rtl ? key === ARROW_RIGHT : key === ARROW_LEFT;
  return doSwitch(orientation, vertical, horizontal);
}
function isMainOrientationToEndKey(key, orientation, rtl) {
  var vertical = key === ARROW_DOWN;
  var horizontal = rtl ? key === ARROW_LEFT : key === ARROW_RIGHT;
  return doSwitch(orientation, vertical, horizontal);
}
function isCrossOrientationOpenKey(key, orientation, rtl) {
  var vertical = rtl ? key === ARROW_LEFT : key === ARROW_RIGHT;
  var horizontal = key === ARROW_DOWN;
  return doSwitch(orientation, vertical, horizontal);
}
function isCrossOrientationCloseKey(key, orientation, rtl) {
  var vertical = rtl ? key === ARROW_RIGHT : key === ARROW_LEFT;
  var horizontal = key === ARROW_UP;
  return doSwitch(orientation, vertical, horizontal);
}
function getMinIndex(listRef) {
  return findNonDisabledIndex(listRef);
}
function getMaxIndex(listRef) {
  return findNonDisabledIndex(listRef, {
    decrement: true,
    startingIndex: listRef.current.length
  });
}

/**
 * Adds focus-managed indexed navigation via arrow keys to a list of items
 * within the floating element.
 * @see https://floating-ui.com/docs/useListNavigation
 */
var useListNavigation = function useListNavigation(_ref, _temp2) {
  var open = _ref.open,
    onOpenChange = _ref.onOpenChange,
    refs = _ref.refs;
  var _ref15 = _temp2 === void 0 ? {
      listRef: {
        current: []
      },
      activeIndex: null,
      onNavigate: function onNavigate() {}
    } : _temp2,
    listRef = _ref15.listRef,
    activeIndex = _ref15.activeIndex,
    onNavigate = _ref15.onNavigate,
    _ref15$enabled = _ref15.enabled,
    enabled = _ref15$enabled === void 0 ? true : _ref15$enabled,
    _ref15$selectedIndex = _ref15.selectedIndex,
    selectedIndex = _ref15$selectedIndex === void 0 ? null : _ref15$selectedIndex,
    _ref15$allowEscape = _ref15.allowEscape,
    allowEscape = _ref15$allowEscape === void 0 ? false : _ref15$allowEscape,
    _ref15$loop = _ref15.loop,
    loop = _ref15$loop === void 0 ? false : _ref15$loop,
    _ref15$nested = _ref15.nested,
    nested = _ref15$nested === void 0 ? false : _ref15$nested,
    _ref15$rtl = _ref15.rtl,
    rtl = _ref15$rtl === void 0 ? false : _ref15$rtl,
    _ref15$virtual = _ref15.virtual,
    virtual = _ref15$virtual === void 0 ? false : _ref15$virtual,
    _ref15$focusItemOnOpe = _ref15.focusItemOnOpen,
    focusItemOnOpen = _ref15$focusItemOnOpe === void 0 ? 'auto' : _ref15$focusItemOnOpe,
    _ref15$focusItemOnHov = _ref15.focusItemOnHover,
    focusItemOnHover = _ref15$focusItemOnHov === void 0 ? true : _ref15$focusItemOnHov,
    _ref15$orientation = _ref15.orientation,
    orientation = _ref15$orientation === void 0 ? 'vertical' : _ref15$orientation;
  if (process.env.NODE_ENV !== "production") {
    if (!loop && allowEscape) {
      console.warn(['Floating UI: `useListNavigation` looping must be enabled to allow', 'escaping.'].join(' '));
    }
  }
  var parentId = useFloatingParentNodeId();
  var tree = useFloatingTree();
  var previousOpen = usePrevious(open);
  var focusOnOpenRef = useRef(focusItemOnOpen);
  var indexRef = useRef(selectedIndex != null ? selectedIndex : -1);
  var keyRef = useRef('');
  var initializedRef = useRef(false);
  var onNavigateRef = useLatestRef(onNavigate);
  var blockPointerLeaveRef = useRef(false);
  var _useState5 = useState(),
    _useState6 = _slicedToArray(_useState5, 2),
    activeId = _useState6[0],
    setActiveId = _useState6[1];
  var focusItem = useCallback(function (listRef, indexRef) {
    if (virtual) {
      var _listRef$current$inde;
      setActiveId((_listRef$current$inde = listRef.current[indexRef.current]) == null ? void 0 : _listRef$current$inde.id);
    } else {
      var _listRef$current$inde2;
      (_listRef$current$inde2 = listRef.current[indexRef.current]) == null ? void 0 : _listRef$current$inde2.focus({
        preventScroll: true
      });
    }
  }, [virtual]);
  index(function () {
    if (!enabled) {
      return;
    }
    if (selectedIndex != null) {
      indexRef.current = selectedIndex;
    }
    if (!previousOpen && open && focusOnOpenRef.current) {
      onNavigateRef.current(indexRef.current);
      focusItem(listRef, indexRef);
    }
  }, [open, previousOpen, selectedIndex, listRef, onNavigateRef, focusItem, enabled]);
  index(function () {
    if (!enabled) {
      return;
    }
    if (open) {
      if (activeIndex === null) {
        if (!previousOpen && focusOnOpenRef.current && selectedIndex == null || allowEscape) {
          indexRef.current = allowEscape ? -1 : getMinIndex(listRef);
          onNavigateRef.current(activeIndex);
          focusItem(listRef, indexRef);
        }
      } else {
        indexRef.current = activeIndex;
        onNavigateRef.current(activeIndex);
        focusItem(listRef, indexRef);
      }
    }
  }, [open, previousOpen, activeIndex, selectedIndex, nested, listRef, onNavigateRef, focusItem, enabled, parentId, allowEscape, refs.floating, tree == null ? void 0 : tree.nodesRef]);
  index(function () {
    if (selectedIndex != null || !enabled) {
      return;
    }
    if (open) {
      if (isMainOrientationKey(keyRef.current, orientation) || focusOnOpenRef.current && (keyRef.current === ' ' || keyRef.current === 'Enter')) {
        var minIndex = getMinIndex(listRef);
        var maxIndex = getMaxIndex(listRef);
        indexRef.current = isMainOrientationToStartKey(keyRef.current, orientation, rtl) ? allowEscape ? listRef.current.length : maxIndex : allowEscape ? -1 : minIndex;
        onNavigateRef.current(indexRef.current);
        focusItem(listRef, indexRef);
      }
    }
    keyRef.current = '';
  }, [open, listRef, selectedIndex, onNavigateRef, focusItem, enabled, orientation, rtl, allowEscape]);
  index(function () {
    if (!enabled) {
      return;
    }
    if (!open && initializedRef.current && selectedIndex != null && isHTMLElement(refs.reference.current)) {
      refs.reference.current.focus();
    }
  }, [refs.reference, selectedIndex, open, enabled]);
  index(function () {
    initializedRef.current = true;
    return function () {
      initializedRef.current = false;
    };
  }, []);
  index(function () {
    if (!enabled) {
      return;
    }
    if (!open) {
      var _ref2;
      if (focusItemOnOpen === 'auto') {
        focusOnOpenRef.current = true;
      }
      indexRef.current = (_ref2 = selectedIndex != null ? selectedIndex : activeIndex) != null ? _ref2 : -1;
      onNavigateRef.current(null);
    }
  }, [open, selectedIndex, activeIndex, enabled, focusItemOnOpen, onNavigateRef]);
  function pointerCheck(event) {
    if (focusItemOnOpen === 'auto') {
      // undefined or '' depending on the browser
      focusOnOpenRef.current = !event.pointerType;
    }
  }
  function onFloatingKeyDown(event) {
    blockPointerLeaveRef.current = true;
    if (nested && isCrossOrientationCloseKey(event.key, orientation, rtl)) {
      stopEvent(event);
      onOpenChange(false);
      if (isHTMLElement(refs.reference.current)) {
        refs.reference.current.focus();
      }
      return;
    }
    var currentIndex = indexRef.current;
    var minIndex = getMinIndex(listRef);
    var maxIndex = getMaxIndex(listRef);
    if (event.key === 'Home') {
      indexRef.current = minIndex;
      onNavigate(indexRef.current);
    }
    if (event.key === 'End') {
      indexRef.current = maxIndex;
      onNavigate(indexRef.current);
    }
    if (isMainOrientationKey(event.key, orientation)) {
      stopEvent(event);
      if (!virtual && event.currentTarget.ownerDocument.activeElement === event.currentTarget) {
        indexRef.current = selectedIndex != null ? selectedIndex : isMainOrientationToEndKey(event.key, orientation, rtl) ? minIndex : maxIndex;
        onNavigate(indexRef.current);
        return;
      }
      if (isMainOrientationToEndKey(event.key, orientation, rtl)) {
        if (loop) {
          indexRef.current = currentIndex >= maxIndex ? allowEscape && currentIndex !== listRef.current.length ? -1 : minIndex : findNonDisabledIndex(listRef, {
            startingIndex: currentIndex
          });
        } else {
          indexRef.current = Math.min(maxIndex, findNonDisabledIndex(listRef, {
            startingIndex: currentIndex
          }));
        }
      } else {
        if (loop) {
          indexRef.current = currentIndex <= minIndex ? allowEscape && currentIndex !== -1 ? listRef.current.length : maxIndex : findNonDisabledIndex(listRef, {
            startingIndex: currentIndex,
            decrement: true
          });
        } else {
          indexRef.current = Math.max(minIndex, findNonDisabledIndex(listRef, {
            startingIndex: currentIndex,
            decrement: true
          }));
        }
      }
      onNavigate(indexRef.current);
    }
  }
  if (!enabled) {
    return {};
  }
  return {
    reference: _objectSpread(_objectSpread({}, virtual && open && activeIndex != null && {
      'aria-activedescendant': activeId
    }), {}, {
      onPointerEnter: pointerCheck,
      onPointerDown: pointerCheck,
      onKeyDown: function onKeyDown(event) {
        blockPointerLeaveRef.current = true;
        if (virtual && open) {
          return onFloatingKeyDown(event);
        }
        if (focusItemOnOpen === 'auto') {
          focusOnOpenRef.current = true;
        }
        keyRef.current = event.key;
        if (nested) {
          if (isCrossOrientationOpenKey(event.key, orientation, rtl)) {
            stopEvent(event);
            if (open) {
              indexRef.current = getMinIndex(listRef);
              onNavigate(indexRef.current);
            } else {
              onOpenChange(true);
            }
          }
          return;
        }
        if (isMainOrientationKey(event.key, orientation)) {
          if (selectedIndex == null) {
            indexRef.current = isMainOrientationToEndKey(event.key, orientation, rtl) ? getMinIndex(listRef) : getMaxIndex(listRef);
          } else {
            indexRef.current = selectedIndex;
          }
          stopEvent(event);
          onOpenChange(true);
          onNavigate(indexRef.current);
        }
        if (virtual && !open) {
          onFloatingKeyDown(event);
        }
      }
    }),
    floating: _objectSpread(_objectSpread({
      'aria-orientation': orientation === 'both' ? undefined : orientation
    }, virtual && activeIndex != null && {
      'aria-activedescendant': activeId
    }), {}, {
      onKeyDown: onFloatingKeyDown,
      onPointerMove: function onPointerMove() {
        blockPointerLeaveRef.current = false;
      }
    }),
    item: _objectSpread({
      onClick: function onClick(_ref3) {
        var currentTarget = _ref3.currentTarget;
        return currentTarget.focus({
          preventScroll: true
        });
      }
    }, focusItemOnHover && {
      onPointerMove: function onPointerMove(_ref4) {
        var currentTarget = _ref4.currentTarget;
        var target = currentTarget;
        if (target) {
          var _index = listRef.current.indexOf(target);
          if (_index !== -1) {
            onNavigate(_index);
          }
        }
      },
      onPointerLeave: function onPointerLeave() {
        if (!blockPointerLeaveRef.current) {
          onNavigate(null);
          if (!virtual) {
            var _refs$floating$curren;
            (_refs$floating$curren = refs.floating.current) == null ? void 0 : _refs$floating$curren.focus({
              preventScroll: true
            });
          } else {
            indexRef.current = -1;
            focusItem(listRef, indexRef);
          }
        }
      }
    })
  };
};

/**
 * Provides a matching callback that can be used to focus an item as the user
 * types, often used in tandem with `useListNavigation()`.
 * @see https://floating-ui.com/docs/useTypeahead
 */
var useTypeahead = function useTypeahead(_ref, _temp) {
  var _ref2;
  var open = _ref.open,
    dataRef = _ref.dataRef;
  var _ref16 = _temp === void 0 ? {
      listRef: {
        current: []
      },
      activeIndex: null
    } : _temp,
    listRef = _ref16.listRef,
    activeIndex = _ref16.activeIndex,
    _ref16$onMatch = _ref16.onMatch,
    onMatch = _ref16$onMatch === void 0 ? function () {} : _ref16$onMatch,
    _ref16$enabled = _ref16.enabled,
    enabled = _ref16$enabled === void 0 ? true : _ref16$enabled,
    _ref16$findMatch = _ref16.findMatch,
    findMatch = _ref16$findMatch === void 0 ? null : _ref16$findMatch,
    _ref16$resetMs = _ref16.resetMs,
    resetMs = _ref16$resetMs === void 0 ? 1000 : _ref16$resetMs,
    _ref16$ignoreKeys = _ref16.ignoreKeys,
    ignoreKeys = _ref16$ignoreKeys === void 0 ? [] : _ref16$ignoreKeys,
    _ref16$selectedIndex = _ref16.selectedIndex,
    selectedIndex = _ref16$selectedIndex === void 0 ? null : _ref16$selectedIndex;
  var timeoutIdRef = useRef();
  var stringRef = useRef('');
  var prevIndexRef = useRef((_ref2 = selectedIndex != null ? selectedIndex : activeIndex) != null ? _ref2 : -1);
  var matchIndexRef = useRef(null);
  index(function () {
    if (open) {
      clearTimeout(timeoutIdRef.current);
      matchIndexRef.current = null;
      stringRef.current = '';
    }
  }, [open]);
  index(function () {
    // Sync arrow key navigation but not typeahead navigation
    if (open && stringRef.current === '') {
      var _ref3;
      prevIndexRef.current = (_ref3 = selectedIndex != null ? selectedIndex : activeIndex) != null ? _ref3 : -1;
    }
  }, [open, selectedIndex, activeIndex]);
  function onKeyDown(event) {
    if (!event.currentTarget.contains(getDocument(event.currentTarget).activeElement)) {
      return;
    }
    if (stringRef.current.length > 0 && stringRef.current[0] !== ' ') {
      dataRef.current.typing = true;
      if (event.key === ' ') {
        stopEvent(event);
      }
    }
    var listContent = listRef.current;
    if (listContent == null || ['Home', 'End', 'Escape', 'Enter', 'Tab', 'ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].concat(_toConsumableArray(ignoreKeys)).includes(event.key)) {
      return;
    } // Bail out if the list contains a word like "llama" or "aaron". TODO:
    // allow it in this case, too.

    var allowRapidSuccessionOfFirstLetter = listContent.every(function (text) {
      var _text$, _text$2;
      return text ? ((_text$ = text[0]) == null ? void 0 : _text$.toLocaleLowerCase()) !== ((_text$2 = text[1]) == null ? void 0 : _text$2.toLocaleLowerCase()) : true;
    }); // Allows the user to cycle through items that start with the same letter
    // in rapid succession

    if (allowRapidSuccessionOfFirstLetter && stringRef.current === event.key) {
      stringRef.current = '';
      prevIndexRef.current = matchIndexRef.current;
    }
    stringRef.current += event.key;
    clearTimeout(timeoutIdRef.current);
    timeoutIdRef.current = setTimeout(function () {
      stringRef.current = '';
      prevIndexRef.current = matchIndexRef.current;
      dataRef.current.typing = false;
    }, resetMs);
    var prevIndex = prevIndexRef.current;
    var orderedList = [].concat(_toConsumableArray(listContent.slice((prevIndex != null ? prevIndex : 0) + 1)), _toConsumableArray(listContent.slice(0, (prevIndex != null ? prevIndex : 0) + 1)));
    var str = findMatch ? findMatch(orderedList, stringRef.current) : orderedList.find(function (text) {
      return (text == null ? void 0 : text.toLocaleLowerCase().indexOf(stringRef.current)) === 0;
    });
    var index = str ? listContent.indexOf(str) : -1;
    if (index !== -1) {
      onMatch(index);
      matchIndexRef.current = index;
    }
  }
  if (!enabled) {
    return {};
  }
  return {
    reference: {
      onKeyDown: onKeyDown
    },
    floating: {
      onKeyDown: onKeyDown
    }
  };
};
function useFloating(_temp) {
  var _ref17 = _temp === void 0 ? {} : _temp,
    _ref17$open = _ref17.open,
    open = _ref17$open === void 0 ? false : _ref17$open,
    _ref17$onOpenChange = _ref17.onOpenChange,
    onOpenChange = _ref17$onOpenChange === void 0 ? function () {} : _ref17$onOpenChange,
    placement = _ref17.placement,
    middleware = _ref17.middleware,
    strategy = _ref17.strategy,
    nodeId = _ref17.nodeId;
  var tree = useFloatingTree();
  var dataRef = useRef({});
  var events = useState(function () {
    return createPubSub();
  })[0];
  var floating = useFloating$1({
    placement: placement,
    middleware: middleware,
    strategy: strategy
  });
  var context = useMemo(function () {
    return _objectSpread(_objectSpread({}, floating), {}, {
      dataRef: dataRef,
      nodeId: nodeId,
      events: events,
      open: open,
      onOpenChange: onOpenChange
    });
  }, [floating, dataRef, nodeId, events, open, onOpenChange]);
  index(function () {
    var node = tree == null ? void 0 : tree.nodesRef.current.find(function (node) {
      return node.id === nodeId;
    });
    if (node) {
      node.context = context;
    }
  });
  return useMemo(function () {
    return _objectSpread({
      context: context
    }, floating);
  }, [floating, context]);
}
export { FloatingDelayGroup, FloatingFocusManager, FloatingNode, FloatingOverlay, FloatingPortal, FloatingTree, safePolygon, useClick, useDelayGroup, useDelayGroupContext, useDismiss, useFloating, useFloatingNodeId, useFloatingParentNodeId, useFloatingTree, useFocus, useFocusTrap, useHover, useId, useInteractions, useListNavigation, useRole, useTypeahead };